import type { RouteApi } from '../useRoute'
import type { AdminSport, AdminSportsbook, Integration, SportDivision } from './api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { assetUrl, categoryLabel, GROUP_ORDER } from '../api'
import { SportSheet, SportSheetBar, useSportSheetOpener } from '../SportSheet'
import { hasRole, useAuthSession } from '../useAuthSession'
import { useResizable } from '../useResizable'
import {
  AdminAuthError,
  fetchAdminSportDivisions,
  fetchAdminSports,
  fetchAdminSportsbooks,
  fetchIntegrations,
  INTEGRATIONS_MIN_ROLE,
  INTEGRATIONS_WRITE_ROLE,
} from './api'
import AuditLog from './AuditLog'
import ConfigsPane from './ConfigsPane'
import IntegrationsPane from './IntegrationsPane'
import NewSportDialog from './NewSportDialog'
import NewSportsbookDialog from './NewSportsbookDialog'
import SparketPanel from './SparketPanel'
import SportEditor from './SportEditor'
import SportsbookEditor from './SportsbookEditor'
import TradingPanel from './TradingPanel'

interface AdminAppProps {
  route: RouteApi
  // 'distribution' (default) renders the full tabbed editor at /distribution.
  // 'trading' renders the same sports sidebar with the TradingPanel edit pane
  // as its own top-level page at /trading/{slug} — no tabs row.
  mode?: 'distribution' | 'trading'
}

// Group order in the sidebar. Categories come straight from the DB
// (sports.category column, populated by migration 0011) — no more
// frontend slug-or-name fallback maps.

const ACCORDION_STORAGE_KEY = 'odds-portal-distribution-accordion'

function readInitialExpanded(): Set<string> {
  // First-time visitors get Motorsport open. Returning visitors keep
  // whatever they last toggled — including "all closed" which we honor
  // via storing the actual list (empty array != undefined).
  if (typeof window === 'undefined')
    return new Set(['MOTORSPORT'])
  try {
    const stored = localStorage.getItem(ACCORDION_STORAGE_KEY)
    if (stored != null) {
      const parsed = JSON.parse(stored) as unknown
      // Single-open accordion — sessions stored before the multi-open
      // behavior was retired may hold several categories; keep the first.
      if (Array.isArray(parsed))
        return new Set(parsed.filter(x => typeof x === 'string').slice(0, 1))
    }
  }
  catch {
    /* ignore */
  }
  return new Set(['MOTORSPORT'])
}

type Tab = 'sports' | 'trading' | 'sportsbooks' | 'integrations' | 'configs' | 'audit'
type DistributionRoute
  = | { tab: 'sports', sportSlug?: string }
    | { tab: 'trading', sportSlug?: string }
    | { tab: 'sportsbooks', sportsbookSlug?: string }
    | { tab: 'integrations', integrationKey?: string }
    | { tab: 'configs' }
    | { tab: 'audit' }

function parseDistributionRoute(path: string, mode: 'distribution' | 'trading'): DistributionRoute {
  const parts = path.toLowerCase().split('/').filter(Boolean)

  // Trading is its own parent page: /trading/{slug}.
  if (mode === 'trading')
    return { tab: 'trading', sportSlug: parts[1] }

  if (parts[1] === 'sports')
    return { tab: 'sports', sportSlug: parts[2] }
  if (parts[1] === 'sportsbooks')
    return { tab: 'sportsbooks', sportsbookSlug: parts[2] }
  if (parts[1] === 'integrations')
    return { tab: 'integrations', integrationKey: parts[2] }
  if (parts[1] === 'configs')
    return { tab: 'configs' }
  if (parts[1] === 'audit')
    return { tab: 'audit' }
  return { tab: 'sports' }
}

function slugifySportsbook(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export default function AdminApp({ route, mode = 'distribution' }: AdminAppProps) {
  const isTradingMode = mode === 'trading'
  // main.tsx's Router only mounts AdminApp for sessions with role >= admin,
  // so we don't probe auth here anymore — if we ever get a 401 mid-session
  // (token expired) we just bubble the error and let the Router re-render on
  // the next auth state change.
  const [tab, setTab] = useState<Tab>(isTradingMode ? 'trading' : 'sports')
  const [sports, setSports] = useState<AdminSport[] | null>(null)
  const [sportsbooks, setSportsbooks] = useState<AdminSportsbook[] | null>(null)
  const [divisions, setDivisions] = useState<SportDivision[]>([])
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null)
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null)
  // INTEGRATIONS tab (migration 0069) — fetched only for sessions that clear
  // INTEGRATIONS_MIN_ROLE; null until then (the tab itself is hidden).
  const [integrations, setIntegrations] = useState<Integration[] | null>(null)
  const [selectedIntegrationKey, setSelectedIntegrationKey] = useState<string | null>(null)
  // Mobile sport/sportsbook sheet — openable from the sticky switcher bar
  // and the header menu's SPORTS row (useSportSheetOpener bus).
  const [sportSheetOpen, setSportSheetOpen] = useState(false)
  useSportSheetOpener(useCallback(() => setSportSheetOpen(true), []))
  const [error, setError] = useState<string | null>(null)
  const [auditTick, setAuditTick] = useState(0)
  const [showNewSport, setShowNewSport] = useState(false)
  const [showNewSportsbook, setShowNewSportsbook] = useState(false)

  const sidebar = useResizable({
    defaultWidth: 280,
    minWidth: 200,
    maxWidth: 600,
    storageKey: 'odds-portal-admin-sidebar-width',
  })

  // Sidebar state — search filter, accordion (expanded category set),
  // both sports + sportsbooks panes share the same search input UX.
  const [searchSports, setSearchSports] = useState('')
  const [searchBooks, setSearchBooks] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(readInitialExpanded)

  function toggleExpanded(cat: string) {
    setExpanded((prev) => {
      // Single-open accordion: opening a group closes whichever was open;
      // clicking the open group closes it (all-closed is a valid state).
      const next = new Set(prev.has(cat) ? [] : [cat])
      try {
        localStorage.setItem(ACCORDION_STORAGE_KEY, JSON.stringify([...next]))
      }
      catch {
        /* ignore */
      }
      return next
    })
  }

  // Editors render in view-mode by default; the Edit button flips them to
  // edit-mode per the role split: sports require admin+, sportsbooks
  // require trading+. Audit log is visible to trading+ (they see the same
  // log as admins). External users never reach this component — the route
  // guard in main.tsx bounces them.
  const { role } = useAuthSession()
  const canEditSports = hasRole(role, 'admin')
  const canEditSportsbooks = hasRole(role, 'trading')
  // Creating a catalog row sits at the sportsbook write tier (trading+),
  // not the sport tier — trading owns the operator catalog.
  const canCreateSportsbooks = hasRole(role, 'trading')
  // Markets Traded is owned by the trading team — editable by trading+ even
  // though the rest of the sport page is admin-gated. The /trading page
  // reuses the same gate (main.tsx already bounces sub-trading roles).
  const canEditMarkets = hasRole(role, 'trading')
  // Carried-by (and who trades each pair) is trading-team knowledge.
  const canEditCarriers = hasRole(role, 'trading')
  const canSeeAudit = hasRole(role, 'trading')
  const canViewConfigs = hasRole(role, 'owner')
  // Owner-only until trading approves the view; the constant lives in
  // admin/api.ts next to its twin in api/integrations.ts (API enforces it).
  const canViewIntegrations = hasRole(role, INTEGRATIONS_MIN_ROLE)
  const canEditIntegrations = hasRole(role, INTEGRATIONS_WRITE_ROLE)

  const distributionRoute = useMemo(
    () => parseDistributionRoute(route.path, mode),
    [route.path, mode],
  )

  // /trading/sparket is the global SPARKET guide page, not a sport —
  // 'sparket' is deliberately absent from the sports catalog, so it gets an
  // explicit carve-out before any sport-slug lookup.
  const isSparket = isTradingMode
    && distributionRoute.tab === 'trading'
    && distributionRoute.sportSlug === 'sparket'

  useEffect(() => {
    setTab(distributionRoute.tab)
    if (distributionRoute.tab === 'audit')
      setAuditTick(x => x + 1)
  }, [distributionRoute.tab])

  useEffect(() => {
    refetchAll()
  }, [])

  function refetchAll(selectSportId?: number) {
    Promise.all([fetchAdminSports(), fetchAdminSportsbooks(), fetchAdminSportDivisions()])
      .then(([s, b, d]) => {
        setSports(s)
        setSportsbooks(b)
        setDivisions(d)
        // selectSportId forces selection of a specific row (used after
        // creating a new sport); otherwise keep the current selection or fall
        // back to the first row.
        setSelectedSportId(prev => selectSportId ?? prev ?? s[0]?.id ?? null)
        setSelectedBookId(prev => prev ?? b[0]?.id ?? null)
      })
      .catch((e) => {
        if (e instanceof AdminAuthError) {
          // Session no longer valid — full reload bounces through Router →
          // login screen since useAuthSession will report no session.
          window.location.reload()
          return
        }
        setError((e as Error).message)
      })
  }

  const selectedSport = sports?.find(s => s.id === selectedSportId) ?? null
  const selectedBook = sportsbooks?.find(b => b.id === selectedBookId) ?? null

  useEffect(() => {
    if ((distributionRoute.tab !== 'sports' && distributionRoute.tab !== 'trading') || !sports)
      return
    if (!('sportSlug' in distributionRoute) || !distributionRoute.sportSlug)
      return

    // SPARKET is not a sport slug — skip the catalog lookup entirely. (The
    // find() below would miss anyway, but the special case stays explicit.)
    if (distributionRoute.sportSlug === 'sparket')
      return

    const match = sports.find(s => s.slug?.toLowerCase() === distributionRoute.sportSlug)
    if (match)
      setSelectedSportId(match.id)
  }, [distributionRoute, sports])

  useEffect(() => {
    if (distributionRoute.tab !== 'sportsbooks' || !sportsbooks || !distributionRoute.sportsbookSlug)
      return

    const match = sportsbooks.find(b => slugifySportsbook(b.name) === distributionRoute.sportsbookSlug)
    if (match)
      setSelectedBookId(match.id)
  }, [distributionRoute, sportsbooks])

  const refetchIntegrations = useCallback(() => {
    if (!canViewIntegrations)
      return
    fetchIntegrations()
      .then(setIntegrations)
      .catch((e) => {
        if (e instanceof AdminAuthError) {
          window.location.reload()
          return
        }
        setError((e as Error).message)
      })
  }, [canViewIntegrations])

  useEffect(() => {
    refetchIntegrations()
  }, [refetchIntegrations])

  useEffect(() => {
    if (distributionRoute.tab !== 'integrations' || !integrations)
      return
    const key = distributionRoute.integrationKey
    const match = key ? integrations.find(i => i.key === key) : undefined
    setSelectedIntegrationKey(match?.key ?? integrations[0]?.key ?? null)
  }, [distributionRoute, integrations])

  const groupedSports = useMemo(() => {
    if (!sports)
      return new Map<string, AdminSport[]>()
    const q = searchSports.trim().toLowerCase()
    const groups = new Map<string, AdminSport[]>()
    for (const cat of GROUP_ORDER) groups.set(cat, [])
    for (const s of sports) {
      if (q && !s.name.toLowerCase().includes(q) && !(s.slug ?? '').toLowerCase().includes(q)) {
        continue
      }
      const label = categoryLabel(s.category)
      if (!groups.has(label))
        groups.set(label, [])
      groups.get(label)!.push(s)
    }
    for (const [k, v] of groups) {
      if (v.length === 0)
        groups.delete(k)
      else v.sort((a, b) => a.name.localeCompare(b.name))
    }
    return groups
  }, [sports, searchSports])

  // When the user is actively searching, force-expand every group with
  // matches so they don't have to click into each category to see hits.
  // When the search is empty, defer to the persisted accordion state.
  const isSearching = searchSports.trim().length > 0
  const effectiveExpanded = isSearching
    ? new Set(groupedSports.keys())
    : expanded

  const filteredSportsbooks = useMemo(() => {
    if (!sportsbooks)
      return null
    const q = searchBooks.trim().toLowerCase()
    const matched = q
      ? sportsbooks.filter(b => b.name.toLowerCase().includes(q))
      : sportsbooks
    const byName = (a: AdminSportsbook, b: AdminSportsbook) => a.name.localeCompare(b.name)
    const active = [...matched].sort(byName)
    return { active, total: active.length }
  }, [sportsbooks, searchBooks])

  function routeForTab(nextTab: Tab): string {
    if (nextTab === 'sports' && selectedSport?.slug) {
      return `/distribution/sports/${selectedSport.slug.toLowerCase()}`
    }
    if (nextTab === 'sportsbooks' && selectedBook) {
      return `/distribution/sportsbooks/${slugifySportsbook(selectedBook.name)}`
    }
    if (nextTab === 'integrations') {
      return selectedIntegrationKey
        ? `/distribution/integrations/${selectedIntegrationKey}`
        : '/distribution/integrations'
    }
    if (nextTab === 'configs')
      return '/distribution/configs'
    if (nextTab === 'audit')
      return '/distribution/audit'
    return '/distribution'
  }

  // Mobile sheet pick — mirrors the sidebar rows' navigation exactly:
  // slugged sports deep-link, slugless fall back to local selection.
  function sheetSelect(key: string) {
    setSportSheetOpen(false)
    if (tab === 'sportsbooks') {
      const b = sportsbooks?.find(x => String(x.id) === key)
      if (b)
        route.navigate(`/distribution/sportsbooks/${slugifySportsbook(b.name)}`)
      return
    }
    if (tab === 'integrations') {
      route.navigate(`/distribution/integrations/${key}`)
      return
    }
    if (key === 'sparket') {
      route.navigate('/trading/sparket')
      return
    }
    const s = sports?.find(x => String(x.id) === key)
    if (!s)
      return
    if (s.slug) {
      route.navigate(isTradingMode
        ? `/trading/${s.slug.toLowerCase()}`
        : `/distribution/sports/${s.slug.toLowerCase()}`)
    }
    else {
      setSelectedSportId(s.id)
    }
  }

  return (
    <div className="ad-root">
      {!isTradingMode && (
        <nav className="ad-tabs">
          {([
            { id: 'sports', label: `SPORTS · ${sports?.length ?? '—'}`, gated: 'none' },
            { id: 'sportsbooks', label: `SPORTSBOOKS · ${sportsbooks?.length ?? '—'}`, gated: 'none' },
            { id: 'integrations', label: `INTEGRATIONS · ${integrations?.length ?? '—'}`, gated: 'integrations' },
            { id: 'configs', label: 'CONFIGS', gated: 'owner' },
            { id: 'audit', label: 'AUDIT LOG', gated: 'audit' },
          ] as const).filter((t) => {
            if (t.gated === 'audit')
              return canSeeAudit
            if (t.gated === 'owner')
              return canViewConfigs
            if (t.gated === 'integrations')
              return canViewIntegrations
            return true
          }).map(t => (
            <button
              type="button"
              key={t.id}
              onClick={() => {
                route.navigate(routeForTab(t.id))
              }}
              className={`b-tab${tab === t.id ? ' is-active' : ''}`}
            >
              <span className="b-mono-dim b-tiny">
                [
                {tab === t.id ? '◼' : ' '}
                ]
              </span>
              {t.label}
            </button>
          ))}
        </nav>
      )}

      {error && (
        <p className="b-mono ad-error">
          ⚠
          {error}
        </p>
      )}

      <main className="ad-main">
        {/* Mobile: the sidebar hides (bm-hide-mobile) and this sticky bar +
            the shared SportSheet take over sport/sportsbook switching. */}
        {(tab === 'sports' || tab === 'trading' || tab === 'sportsbooks' || tab === 'integrations') && (
          <SportSheetBar
            label={tab === 'sportsbooks'
              ? selectedBook?.name ?? null
              : tab === 'integrations'
                ? integrations?.find(i => i.key === selectedIntegrationKey)?.name ?? null
                : isSparket ? 'SPARKET' : selectedSport?.name ?? null}
            placeholder={tab === 'sportsbooks'
              ? 'SELECT SPORTSBOOK'
              : tab === 'integrations' ? 'SELECT INTEGRATION' : 'SELECT SPORT'}
            onOpen={() => setSportSheetOpen(true)}
          />
        )}
        {/* SPORTS (distribution mode) and the /trading page share the sports
            sidebar — only the edit pane differs (approvals editor vs
            trading-notes panel). */}
        {(tab === 'sports' || tab === 'trading') && (
          <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
            <aside className="ad-list-pane bm-hide-mobile">
              <div className="ad-list-search">
                <span className="b-mono-dim">/</span>
                <input
                  value={searchSports}
                  onChange={e => setSearchSports(e.target.value)}
                  placeholder="filter sports…"
                  className="ad-list-search-input"
                  spellCheck={false}
                />
                {searchSports && (
                  <button
                    type="button"
                    onClick={() => setSearchSports('')}
                    className="ad-list-search-clear"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
                {canEditSports && tab === 'sports' && (
                  <button
                    type="button"
                    onClick={() => setShowNewSport(true)}
                    className="b-btn b-btn-primary ad-list-new-btn"
                  >
                    + NEW
                  </button>
                )}
              </div>
              {!sports
                ? <p className="b-mono-dim">LOADING…</p>
                : groupedSports.size === 0
                  ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
                  : [...groupedSports.entries()].map(([cat, items]) => {
                      const open = effectiveExpanded.has(cat)
                      return (
                        <div key={cat} className={`ad-list-group${open ? ' is-open' : ''}`}>
                          <button
                            type="button"
                            onClick={() => toggleExpanded(cat)}
                            className="ad-list-group-head"
                            aria-expanded={open}
                          >
                            <span className="ad-list-group-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
                            <span className="ad-list-group-label">{cat}</span>
                            <span className="ad-list-group-count">{items.length}</span>
                          </button>
                          {open && items.map((s) => {
                            // No sport row reads active while SPARKET is open.
                            const active = !isSparket && s.id === selectedSportId
                            const stateCount = s.sport_states.length
                            const intlCount = s.sport_international.length
                            return (
                              <button
                                type="button"
                                key={s.id}
                                onClick={() => {
                                  if (s.slug) {
                                    route.navigate(isTradingMode
                                      ? `/trading/${s.slug.toLowerCase()}`
                                      : `/distribution/sports/${s.slug.toLowerCase()}`)
                                  }
                                  else {
                                    setSelectedSportId(s.id)
                                  }
                                }}
                                className={`ad-list-row${active ? ' is-active' : ''}`}
                              >
                                {s.logo_url && (
                                  <img
                                    src={assetUrl(s.logo_url)}
                                    alt=""
                                    aria-hidden="true"
                                    loading="lazy"
                                    className="ad-list-logo"
                                  />
                                )}
                                <span className="ad-list-name">
                                  {s.name}
                                  {s.slug && (
                                    <span className="b-mono-dim ad-list-slug">
                                      {' '}
                                      ·
                                      {s.slug}
                                    </span>
                                  )}
                                </span>
                                <span className="b-mono-dim b-tiny">
                                  {stateCount}
                                  S /
                                  {' '}
                                  {intlCount}
                                  I
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      )
                    })}
              {/* SPARKET — global (non-sport) trading guide, pinned below
                  the category groups. Trading page only. */}
              {isTradingMode && (
                <button
                  type="button"
                  onClick={() => route.navigate('/trading/sparket')}
                  className={`ad-list-row ad-list-row--sparket${isSparket ? ' is-active' : ''}`}
                >
                  <span className="ad-list-name">SPARKET</span>
                  <span className="b-mono-dim b-tiny">GUIDE</span>
                </button>
              )}
              <button
                type="button"
                className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
                onMouseDown={sidebar.onMouseDown}
                aria-label="Resize sidebar"
              />
            </aside>
            <div className="ad-edit-pane">
              {tab === 'sports' && selectedSport && sportsbooks && (
                <SportEditor
                  key={selectedSport.id}
                  sport={selectedSport}
                  sportsbooks={sportsbooks}
                  canEdit={canEditSports}
                  canEditMarkets={canEditMarkets}
                  canEditCarriers={canEditCarriers}
                  canViewIntegrations={canViewIntegrations}
                  canEditIntegrations={canEditIntegrations}
                  onSaved={() => { refetchAll() }}
                />
              )}
              {tab === 'trading' && (
                isSparket
                  ? <SparketPanel canEdit={canEditMarkets} />
                  : selectedSport && (
                      <TradingPanel
                        key={selectedSport.id}
                        sport={selectedSport}
                        canEdit={canEditMarkets}
                      />
                    )
              )}
            </div>
          </div>
        )}

        {tab === 'sportsbooks' && (
          <div className="ad-pane" style={{ '--sidebar-width': `${sidebar.width}px` } as React.CSSProperties}>
            <aside className="ad-list-pane bm-hide-mobile">
              <div className="ad-list-search">
                <span className="b-mono-dim">/</span>
                <input
                  value={searchBooks}
                  onChange={e => setSearchBooks(e.target.value)}
                  placeholder="filter sportsbooks…"
                  className="ad-list-search-input"
                  spellCheck={false}
                />
                {searchBooks && (
                  <button
                    type="button"
                    onClick={() => setSearchBooks('')}
                    className="ad-list-search-clear"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
                {canCreateSportsbooks && (
                  <button
                    type="button"
                    onClick={() => setShowNewSportsbook(true)}
                    className="b-btn b-btn-primary ad-list-new-btn"
                  >
                    + NEW
                  </button>
                )}
              </div>
              {!filteredSportsbooks
                ? <p className="b-mono-dim">LOADING…</p>
                : filteredSportsbooks.total === 0
                  ? <p className="b-mono-dim ad-list-empty-search">no matches</p>
                  : (
                      <>
                        {filteredSportsbooks.active.map((b) => {
                          const active = b.id === selectedBookId
                          const stateCount = b.sportsbook_states.length
                          const intlCount = b.sportsbook_international.length
                          return (
                            <button
                              type="button"
                              key={b.id}
                              onClick={() => route.navigate(`/distribution/sportsbooks/${slugifySportsbook(b.name)}`)}
                              className={`ad-list-row${active ? ' is-active' : ''}`}
                            >
                              <span className="ad-list-name">{b.name}</span>
                              <span className="b-mono-dim b-tiny">
                                {stateCount}
                                S /
                                {' '}
                                {intlCount}
                                I
                              </span>
                            </button>
                          )
                        })}
                      </>
                    )}
              <button
                type="button"
                className={`ad-resize-handle${sidebar.isDragging ? ' is-dragging' : ''}`}
                onMouseDown={sidebar.onMouseDown}
                aria-label="Resize sidebar"
              />
            </aside>
            <div className="ad-edit-pane">
              {selectedBook && sports && (
                <SportsbookEditor
                  key={selectedBook.id}
                  sportsbook={selectedBook}
                  sports={sports}
                  divisions={divisions}
                  canEdit={canEditSportsbooks}
                  onSaved={() => { refetchAll() }}
                />
              )}
            </div>
          </div>
        )}

        {tab === 'integrations' && canViewIntegrations && (
          <IntegrationsPane
            integrations={integrations}
            sports={sports}
            selectedKey={selectedIntegrationKey}
            canEdit={canEditIntegrations}
            onSelect={key => route.navigate(`/distribution/integrations/${key}`)}
            onOpenSport={slug => route.navigate(`/distribution/sports/${slug.toLowerCase()}`)}
            onChanged={refetchIntegrations}
            sidebar={sidebar}
          />
        )}

        {tab === 'configs' && canViewConfigs && (
          <ConfigsPane />
        )}

        {tab === 'audit' && (
          <div className="ad-audit-pane">
            <AuditLog
              key={auditTick}
              sports={sports ?? []}
              sportsbooks={sportsbooks ?? []}
              divisions={divisions}
            />
          </div>
        )}
      </main>

      {sportSheetOpen && (
        <SportSheet
          title={tab === 'sportsbooks'
            ? 'SELECT SPORTSBOOK'
            : tab === 'integrations' ? 'SELECT INTEGRATION' : 'SELECT SPORT'}
          items={tab === 'integrations'
            ? (integrations ?? []).map(i => ({
                key: i.key,
                label: i.name,
                sub: i.key,
                right: (
                  <span className="b-mono-dim b-tiny">
                    {i.leagues.length}
                    {' '}
                    LEAGUES
                  </span>
                ),
              }))
            : tab === 'sportsbooks'
            ? (sportsbooks ?? []).map(b => ({
                key: String(b.id),
                label: b.name,
                right: (
                  <span className="b-mono-dim b-tiny">
                    {b.sportsbook_states.length}
                    S /
                    {' '}
                    {b.sportsbook_international.length}
                    I
                  </span>
                ),
              }))
            : [
                ...(sports ?? []).map(s => ({
                  key: String(s.id),
                  label: s.name,
                  category: s.category,
                  sub: s.slug ?? undefined,
                  right: (
                    <span className="b-mono-dim b-tiny">
                      {s.sport_states.length}
                      S /
                      {' '}
                      {s.sport_international.length}
                      I
                    </span>
                  ),
                })),
                ...(isTradingMode
                  ? [{ key: 'sparket', label: 'SPARKET', pinned: true, right: <span className="b-mono-dim b-tiny">GUIDE</span> }]
                  : []),
              ]}
          selectedKey={tab === 'integrations'
            ? selectedIntegrationKey
            : tab === 'sportsbooks'
              ? (selectedBookId != null ? String(selectedBookId) : null)
              : isSparket ? 'sparket' : (selectedSportId != null ? String(selectedSportId) : null)}
          onSelect={sheetSelect}
          onClose={() => setSportSheetOpen(false)}
        />
      )}

      {showNewSport && (
        <NewSportDialog
          onClose={() => setShowNewSport(false)}
          onCreated={(id) => {
            setShowNewSport(false)
            setTab('sports')
            refetchAll(id)
          }}
        />
      )}
      {showNewSportsbook && (
        <NewSportsbookDialog
          onClose={() => setShowNewSportsbook(false)}
          onCreated={({ name }) => {
            setShowNewSportsbook(false)
            route.navigate(`/distribution/sportsbooks/${slugifySportsbook(name)}`)
            refetchAll()
          }}
        />
      )}
    </div>
  )
}
